import Button from 'apollo-react/components/Button';
import Card from 'apollo-react/components/Card';
import CardContent from 'apollo-react/components/CardContent';
import CardHeader from 'apollo-react/components/CardHeader';
import Container from 'apollo-react/components/Container';
import Grid from 'apollo-react/components/Grid';
import TextField from 'apollo-react/components/TextField';
import Typography from 'apollo-react/components/Typography';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import ConfirmDialog from '../common/ConfirmDialog';
import { API_URL, OBJ_ROLE, SYS_ROLE } from '../common/Constants';
import DetailDialog from '../common/DetailDialog';
import Dialog from '../common/Dialog';
import Loading from '../common/Loading';
import { getApi, postApi, setURL, redirectToLogin } from '../common/Utils';
import { Box } from '@mui/material';
import './SettingSharedFolder.scss';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Trash from 'apollo-react-icons/Trash';
import Radio from 'apollo-react/components/Radio';
import RadioGroup from 'apollo-react/components/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import shareUserList from '../json/shareUserList.json';

function SettingSharedFolder() {
	const location = useLocation();
	const [t] = useTranslation();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(true);
	const DIALOG_STATUS = {
		NO_DIALOG: '0',
		OK_DIALOG: '1',
		DETAIL_DIALOG: '2',
		SAVE_CONFIRM_DIALOG: '3',
		DELETE_FOLDER_CONFIRM_DIALOG: '4',
		COMPLETE_DIALOG: '5',
		OK_DIALOG_BACK: '6',
		DETAIL_DIALOG_BACK: '7',
		SAVE_SUCCESS_DIALOG: '8',
		DELETE_SYSTEM_USER_CONFIRM_DIALOG: '9', // 追加
		DELETE_USER_CONFIRM_DIALOG: '10',
	};

	// 確認ダイアログ表示用フラグ												
	const [dialogStatus, setDialogStatus] = useState(DIALOG_STATUS.NO_DIALOG);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogContent, setDialogContent] = useState('');
	const [detailContent, setDetailContent] = useState('');
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [confirmDialogContent, setConfirmDialogContent] = useState('');
	const [duplicateUserIds, setDuplicateUserIds] = useState(new Set());

	// 各種権限												
	const [permissionSelector, setPermissionSelector] = useState(false);
	const [userDelete, setUserDelete] = useState(false);
	const [userCancelBtn, setUserCancelBtn] = useState(false);
	const [autoUserAddBtn, setAutoUserAddBtn] = useState(false);
	const [manualUserAddBtn, setManualUserAddBtn] = useState(false);

	// フォルダ管理画面orフォルダ作成画面からの値をもとに値をセットする												
	const { auditName, auditCode, orgName, orgLevel0Code, projName,projectCode } = location.state || {};

	// allUserListが定義された後に表示用データを分離
    const [allUserList, setAllUserList] = useState([]);
    
    // 表示用にデータを分離（allUserListの定義後に移動）
    const systemUsers = allUserList.filter(user => user.usrKbn === '0');
	const generalUsers = allUserList.filter(user => user.usrKbn === '1');
    const initialSystemUsers = systemUsers.filter(user => user.isInitial);
    const addedSystemUsers = systemUsers.filter(user => !user.isInitial);
    const initialGeneralUsers = generalUsers.filter(user => user.isInitial);
    const addedGeneralUsers = generalUsers.filter(user => !user.isInitial);

	// 権限設定情報の入力内容（APIで使用する）												
	const [formValues, setFormValues] = useState({
		auditCode: auditCode,
		orgLevel0Code: orgLevel0Code,
		projectCode: projectCode,
		auditName: auditName,
		orgName: orgName,
		projName: projName,
	});

	/**												
	 * 共通エラー処理												
	 * @param {object} error エラーオブジェクト												
	 * @param {string} detailErrorMessage 詳細ダイアログに設定するエラーメッセージ												
	 */
	const commonError = (error, detailErrorMessage) => {
		if (error.name === 'MaintenanceApiError') {
			navigate('/', { state: { maintenanceFlg: true, } });
		} else if (error.name === 'CustomApiError') {

			const target = /該当データ/;
			if (error.message.match(target)) {
				const message = t('message.1005000');
				setDialogStatus(DIALOG_STATUS.OK_DIALOG);
				setDialogOpen(true);
				setDialogContent(message);
				return;
			}

			const message = error.message;
			setDialogStatus(DIALOG_STATUS.OK_DIALOG);
			setDialogOpen(true);
			setDialogContent(message);
		} else {
			setDialogStatus(DIALOG_STATUS.DETAIL_DIALOG);
			setDialogOpen(true);
			setDialogContent(detailErrorMessage);
			setDetailContent(error.stack);
		}
	};									

	/**												
	 * ダイアログでOKボタン押下時の処理												
	 */
	const onOk = () => {
		setDialogStatus(DIALOG_STATUS.NO_DIALOG);
		setDialogOpen(false);
		if (dialogStatus === DIALOG_STATUS.OK_DIALOG_BACK || dialogStatus === DIALOG_STATUS.DETAIL_DIALOG_BACK) {
			navigate('/folderManagement');
		}
	};

	/**												
	 * 確認ダイアログでキャンセルボタン押下時の処理												
	 */
	const onCancel = () => {
		setDialogStatus(DIALOG_STATUS.NO_DIALOG);
		setConfirmDialogOpen(false);
	};

	// 保存ボタン押下処理												
	const saveProcess = () => {
		setDialogStatus(DIALOG_STATUS.NO_DIALOG);
		setConfirmDialogOpen(false);

		// 変更があるデータのみを抽出
		const changedUsers = allUserList.filter(user => user.changeType !== '0');
    
		if (changedUsers.length === 0) {
			// 変更なしの場合の処理
			setDialogStatus(DIALOG_STATUS.OK_DIALOG);
			const message = format(t('message.1007070'), "");
			setDialogOpen(true);
			setDialogContent(message);
			return;
		}
		
		// API送信用データの準備
		const apiData = changedUsers.map(({ id, isInitial, isDeleted, ...rest }) => {
			const userData = { ...rest };
			
			// 削除の場合、usrKbnを"2"にセット
			if (isDeleted) {
				userData.usrKbn = "2";
			}
			
			// 新規追加の場合、usrDomainBeforeを""にセット
			if (userData.changeType === '1') {
				userData.usrDomainBefore = "";
			}
			
			return userData;
		});
		
		// 変更がある場合の処理を続行
		const url = setURL(API_URL.SHARE_ACCESS_UPSERT);
		let formData = new FormData();
		formData.append('auditCode', formValues.auditCode);
		formData.append('auditName', formValues.auditName);
		formData.append('orgLevel0Code', formValues.orgLevel0Code);
		formData.append('orgName', formValues.orgName);
		formData.append('projCode', formValues.projectCode);
		formData.append('projName', formValues.projName);

		// Base64エンコーディングを使用して特殊記号をエスケープし、WAFルールに引っかからないようにする
		const encodedData = btoa(JSON.stringify(apiData));
		formData.append('shareAccessList', encodedData);

		const postApiParam = {
			url: url,
			sysRole: SYS_ROLE.UPLOAD_SCHEDULE,
			body: formData,
			success: saveSuccess,
			error: saveError,
		};
		postApi(postApiParam);
	}
	/**												
	 * キャンセルボタン押下時の処理												
	 */
	const handleCancelClick = () => {
		commonBack();
	}

	// 保存及び更新処理完了時												
	function saveSuccess() {
		setDialogStatus(DIALOG_STATUS.SAVE_SUCCESS_DIALOG);
		const message = format(t('message.1007060'), "");
		setDialogOpen(true);
		setDialogContent(message);
	};

	/**												
	 * 保存及び更新処理の失敗時の処理												
	 * @param {object} error エラーオブジェクト												
	 */
	function saveError(error) {
		const detailErrorMessage = '保存及び更新処理でエラーが発生しました。\n(An error occurred during the select item process.)';
		commonError(error, detailErrorMessage);

		//認証期限切れの場合は再ログイン											
		redirectToLogin();
	};

	/**											F	
	 * スケジュール画面へ移動												
	 */
	const commonBack = () => {
		// フォルダ管理画面へ遷移	
		const param = {
			// org 関連
			orgLevel0Code: formValues.orgLevel0Code,
			orgName: formValues.orgName,

			// audit 関連
			auditCode: formValues.auditCode,
			auditName: formValues.auditName,

			// folderName
			projName: formValues.projName,
			projCode: formValues.projectCode
		};

		// state オブジェクトを使用して画面遷移
		navigate('/uploadschedule', { state: param });										
	};

	const onFinished = () => {

		const param = {
			auditName: formValues.auditName,
			auditCode: formValues.auditCode,
			orgName: formValues.orgName,
			orgLevel0Code: formValues.orgLevel0Code,
			projectCode: formValues.projectCode,
			projName: formValues.projName
		};

		// URLにパラメータを追加											
		let newUrl = new URL(window.location.href);
		Object.keys(param).forEach(key => newUrl.searchParams.set(key, param[key]));

		// 新しいURLにリダイレクト											
		window.location.href = newUrl.toString();
	}

	/**												
	 * 保存ボタン押下時の処理												
	 */
	const handleSaveClick = () => {
		setDialogStatus(DIALOG_STATUS.SAVE_CONFIRM_DIALOG);
		const message = t('message.1007050');
		setConfirmDialogOpen(true);
		setConfirmDialogContent(message);
	}

	// 削除チェックボックス変更
	const handleDeleteChange = (userId, checked) => {
		updateUserInList(userId, { 
			isDeleted: checked
		});
	};

	// 取消ボタン（完全削除）
	const handleRemoveUser = (userId) => {
		setAllUserList(prev => prev.filter(user => user.id !== userId));
	};

	/**												
	 * 選択したサービスの初期表示ユーザー取得APIの成功時の処理												
	 */
	function usrListSuccess(response) {
		const initialUserList = response.map(item => ({
			id: `initial_${item.usrId}_${item.usrKbn}`,
			usrDomainBefore: item.usrDomain,
			usrDomainAfter: item.usrDomain,
			usrIdBefore: item.usrId,
			usrIdAfter: item.usrId,
			usrKbn: item.usrKbn,
			changeType: '0', // 初期状態は変更なし
			isInitial: true,
			isDeleted: false
		}));
		
		setAllUserList(initialUserList);
	}

	/**												
	 * 選択したサービスの初期表示ユーザー取得APIの失敗時の処理												
	 * @param {object} error エラーオブジェクト												
	 */
	function usrListError(error) {
		const detailErrorMessage = 'ユーザー情報取得処理でエラーが発生しました。\n(An error occurred during the getrolelist process.)';
		commonError(error, detailErrorMessage);

		//認証期限切れの場合は再ログイン											
		redirectToLogin();
	};

	// // C#のプレースホルダーをJavascriptで使用するための処理												
	const format = (str, ...args) => {
		for (const [i, arg] of args.entries()) {
			const regExp = new RegExp(`\\{${i}\\}`, 'g')
			str = str.replace(regExp, arg)
		}
		return str
	}

	useEffect(() => {
		(async () => {
			if (window.localStorage.getItem(['token']) === null) {
				navigate('/')
				return;
			}

			// リダイレクトしてきた場合の値セット										
			let reparams = new URLSearchParams(window.location.search);

			// 各パラメータを取得										
			if (reparams.get('auditName')) {
				formValues.auditName = reparams.get('auditName');
			}
			if (reparams.get('auditCode')) {
				formValues.auditCode = reparams.get('auditCode');
			}
			if (reparams.get('orgName')) {
				formValues.orgName = reparams.get('orgName');
			}
			if (reparams.get('orgLevel0Code')) {
				formValues.orgLevel0Code = reparams.get('orgLevel0Code');
			}
			if (reparams.get('projectCode')) {
				formValues.projectCode = reparams.get('projectCode');
			}
			if (reparams.get('projName')) {
				formValues.projName = reparams.get('projName');
			}

			setUserDelete(true);
			setUserCancelBtn(true);
			setAutoUserAddBtn(true);
			setManualUserAddBtn(true);
			
			// ユーザー取得
			// auditcode projcodeのパラメータ渡す
			const url = setURL(API_URL.SHARE_ACCESS_USER_LIST) + '?';
			const params = {};
			params['auditCode'] = formValues.auditCode;
			params['projCode'] = formValues.projectCode;
			const urlSearchParam = String(new URLSearchParams(params));
			const getApiParam = {
				url: url + urlSearchParam,
				sysRole: SYS_ROLE.UPLOAD_SCHEDULE,
				success: usrListSuccess,
				error: usrListError,
				json: shareUserList,
			}
			await getApi(getApiParam);
			
			setIsLoading(false);
		})();
	}, []);

	// システムユーザー追加
	const onAddSystemUser = () => {
		const newUser = {
			id: `new_system_${Date.now()}`,
			usrDomainBefore: '0',
			usrDomainAfter: '0',
			usrIdBefore: '',
			usrIdAfter: '',
			usrKbn: '0',
			changeType: '1', // 新規追加
			isInitial: false,
			isDeleted: false
		};
		
		setAllUserList(prev => [...prev, newUser]);
	};

	// 一般ユーザー追加
	const onAddUser = () => {
		const newUser = {
			id: `new_user_${Date.now()}`,
			usrDomainBefore: '1',
			usrDomainAfter: '1',
			usrIdBefore: '',
			usrIdAfter: '',
			usrKbn: '1',
			changeType: '1', // 新規追加
			isInitial: false,
			isDeleted: false
		};
		
		setAllUserList(prev => [...prev, newUser]);
	};

	// 統一的な更新関数
	const updateUserInList = (userId, updates) => {
		setAllUserList(prev => prev.map(user => {
			if (user.id === userId) {
				const updatedUser = { ...user, ...updates };
				
				// 変更判定ロジック
				if (updatedUser.changeType !== '1') { // 新規追加以外の場合
					const hasChanges = 
						updatedUser.usrDomainAfter !== updatedUser.usrDomainBefore ||
						updatedUser.usrIdAfter !== updatedUser.usrIdBefore;
					
					if (updatedUser.isDeleted) {
						updatedUser.changeType = '2'; // 削除
					} else if (hasChanges) {
						updatedUser.changeType = '3'; // 変更
					} else {
						updatedUser.changeType = '0'; // 変更なし
					}
				}
				
				return updatedUser;
			}
			return user;
		}));
	};

	const [isVisible, setIsVisible] = useState(true);

	const [hasEmptyFields,setHasEmptyFields] = useState(false);
	
	useEffect(() => {
		const hasEmptyFields = allUserList.some(user => 
			!user.isDeleted && (!user.usrIdAfter || user.usrIdAfter.trim() === '')
		);
		setHasEmptyFields(hasEmptyFields);
	}, [allUserList]);

	useEffect(() => {
		// 削除されていないユーザーのusrIdAfterを取得
		const activeUserIds = allUserList
			.filter(user => !user.isDeleted && user.usrIdAfter && user.usrIdAfter.trim() !== '')
			.map(user => user.usrIdAfter.trim());
		
		// 重複するIDを検出
		const duplicates = new Set();
		const seen = new Set();
		
		activeUserIds.forEach(id => {
			if (seen.has(id)) {
				duplicates.add(id);
			} else {
				seen.add(id);
			}
		});
		
		setDuplicateUserIds(duplicates);
	}, [allUserList]);

	// 重複チェック関数
	const isDuplicateUserId = (userId) => {
		if (!userId || userId.trim() === '') return false;
		return duplicateUserIds.has(userId.trim());
	};

	// hasEmptyFieldsの更新（重複チェックも含める）
	useEffect(() => {
		const hasEmptyFields = allUserList.some(user => 
			!user.isDeleted && (!user.usrIdAfter || user.usrIdAfter.trim() === '')
		);
		const hasDuplicates = duplicateUserIds.size > 0;
		setHasEmptyFields(hasEmptyFields || hasDuplicates);
	}, [allUserList, duplicateUserIds]);
	
	if (isLoading) {
		// ロード中の場合はローディング画面											
		return (
			<Loading />
		);
	} else {
		return (
			<Container className="settingSharedFolder" maxWidth="lg">

				<Card className="card">
					<CardHeader
						title={t('settingSharedFolder')}
						className="custom-card-header"
					/>
					<CardContent>
						<Grid container>
							{/* サービス */}
							<Box display="flex" alignItems="center" width="100%">
								<Typography
									className="label"
								>
									{t('service')}
								</Typography>
								<TextField
									value={formValues.auditName}
									className="textField"
									disabled
								/>
							</Box>
							{/* サービス　ここまで */}

							{/* 企業 */}
							<Box display="flex" alignItems="center" width="100%">
								<Typography
									className="label"
								>
									{t('org')}
								</Typography>
								<TextField
									value={formValues.orgName}
									className="textField"
									disabled
								/>
							</Box>
							{/* 企業　ここまで */}

							{/* フォルダ */}
							<Box display="flex" alignItems="center" width="100%">
								<Typography className="label">{t('proj')}</Typography>
								<TextField
									value={formValues.projName}
									className="textField"
									disabled
								/>
							</Box>
							{/* フォルダ　ここまで */}
							{/* システムユーザID（自動アップロード） */}
							<Box display="flex" className="header" alignItems="center" width="100%">
								<Typography
									className="labelHeaderUser"
								>
									{t('systemUserID')}
								</Typography>

								<Typography
									className="labelHeaderDelete"
								>
									{t('delete')}
								</Typography>

								<Typography
									className="labelHeaderCancel"
								>
									取消
								</Typography>
							</Box>

							{/* システムユーザー（自動アップロード）初期表示分 */}
							{initialSystemUsers.map((user, index) => (
								<Box display="flex" key={user.id} alignItems="center" width="100%" className="userRow">
									<FormControl component="fieldset" className="radioGroupContainer">
										<RadioGroup
											className='radioGroup'
											name={`systemUser-${user.id}`}
											value={user.usrDomainAfter || "0"}
											onChange={(e) => {
												updateUserInList(user.id, {
													usrDomainAfter: e.target.value
												});
											}}
										>
											<FormControlLabel value="0" control={<Radio />} label="Internal" />
											<FormControlLabel value="1" control={<Radio />} label="QAMR-Accounts" />
										</RadioGroup>
									</FormControl>
									
									<TextField
										className="textFieldUser"
										value={user.usrIdAfter}
										helperText={
											!user.usrIdAfter ? format(t('message.1000220'), t('userId')) :
											isDuplicateUserId(user.usrIdAfter) ? t('message.1007071') : ''
										}
										FormHelperTextProps={{ className: 'red-helper-text' }}
										onChange={(e) => {
											updateUserInList(user.id, {
												usrIdAfter: e.target.value
											});
										}}
									/>
									
									<div className='deleteColumn'>
										<Checkbox
											className='deleteCheckBox'
											checked={user.isDeleted}
											onChange={(e) => handleDeleteChange(user.id, e.target.checked)}
										/>
									</div>
									
									<div className='cancelColumn'>
										{/* 初期表示分では何も表示しない */}
									</div>
								</Box>
							))}

							{/* システムユーザー（自動アップロード）追加分 */}
							{addedSystemUsers.map((user, index) => (
								<Box className='fieldRow userRow' display="flex" key={user.id} alignItems="center" width="100%">
									<FormControl component="fieldset" className="radioGroupContainer">
										<RadioGroup
											className='radioGroup'
											name={`addedUser-${user.id}`}
											value={user.usrDomainAfter || "0"}
											onChange={(e) => {
												updateUserInList(user.id, {
													usrDomainAfter: e.target.value
												});
											}}
										>
											<FormControlLabel value="0" control={<Radio />} label="Internal" />
											<FormControlLabel value="1" control={<Radio />} label="QAMR-Accounts" />
										</RadioGroup>
									</FormControl>
									
									<TextField
										className="textFieldUser"
										value={user.usrIdAfter}
										helperText={
											!user.usrIdAfter ? format(t('message.1000220'), t('userId')) :
											isDuplicateUserId(user.usrIdAfter) ? t('message.1007071') : ''
										}
										FormHelperTextProps={{ className: 'red-helper-text' }}
										onChange={(e) => {
											updateUserInList(user.id, {
												usrIdAfter: e.target.value
											});
										}}
									/>
									
									<div className='deleteColumn'>
										{/* 追加分では何も表示しない */}
									</div>
									
									<div className='cancelColumn'>
										<IconButton
											className='trashBtn'
											onClick={() => handleRemoveUser(user.id)}
											disabled={!userCancelBtn}
										>
											<Trash />
										</IconButton>
									</div>
								</Box>
							))}

							<Button
								className="userSearchBtn"
								variant='secondary'
								onClick={onAddSystemUser}
							>
								{t('addSystemUser')}
							</Button>

							{/* 空白行を追加 */}
							<div className="spacer-large" />

							<Box display="flex" className="header" alignItems="center" width="100%">
								<Typography className="labelHeaderUser">
									{t('shareUserID')}
								</Typography>
								<Typography className="labelHeaderDelete">
									{t('delete')}
								</Typography>
								<Typography className="labelHeaderCancel">
									取消
								</Typography>
							</Box>
							<br/>

							{/* ユーザーID（手動アップロード）初期表示分 */}
							{initialGeneralUsers.map((user, index) => (
								<Box display="flex" key={user.id} alignItems="center" width="100%" className="userRow">
									<FormControl component="fieldset" className="radioGroupContainer">
										<RadioGroup
											className='radioGroup'
											name={`generalUser-${user.id}`}
											value={user.usrDomainAfter || "1"}
											onChange={(e) => {
												updateUserInList(user.id, {
													usrDomainAfter: e.target.value
												});
											}}
										>
											<FormControlLabel value="0" control={<Radio />} label="Internal" />
											<FormControlLabel value="1" control={<Radio />} label="QAMR-Accounts" />
										</RadioGroup>
									</FormControl>

									<TextField
										className="textFieldUser"
										value={user.usrIdAfter}
										helperText={
											!user.usrIdAfter ? format(t('message.1000220'), t('userId')) :
											isDuplicateUserId(user.usrIdAfter) ? t('message.1007071') : ''
										}
										FormHelperTextProps={{ className: 'red-helper-text' }}
										onChange={(e) => {
											updateUserInList(user.id, {
												usrIdAfter: e.target.value
											});
										}}
									/>

									<div className='deleteColumn'>
										<Checkbox
											className='deleteCheckBox'
											checked={user.isDeleted}
											onChange={(e) => handleDeleteChange(user.id, e.target.checked)}
										/>
									</div>
									
									<div className='cancelColumn'>
										{/* 初期表示分では何も表示しない */}
									</div>
								</Box>
							))}

							{/* ユーザーID（手動アップロード）追加分 */}
							{addedGeneralUsers.map((user, index) => (
								<Box className='fieldRow userRow' display="flex" key={user.id} alignItems="center" width="100%">
									<FormControl component="fieldset" className="radioGroupContainer">
										<RadioGroup
											className='radioGroup'
											name={`addedGeneralUser-${user.id}`}
											value={user.usrDomainAfter || "1"}
											onChange={(e) => {
												updateUserInList(user.id, {
													usrDomainAfter: e.target.value
												});
											}}
										>
											<FormControlLabel value="0" control={<Radio />} label="Internal" />
											<FormControlLabel value="1" control={<Radio />} label="QAMR-Accounts" />
										</RadioGroup>
									</FormControl>
									
									<TextField
										className="textFieldUser"
										value={user.usrIdAfter}
										helperText={
											!user.usrIdAfter ? format(t('message.1000220'), t('userId')) :
											isDuplicateUserId(user.usrIdAfter) ? t('message.1007071') : ''
										}
										FormHelperTextProps={{ className: 'red-helper-text' }}
										onChange={(e) => {
											updateUserInList(user.id, {
												usrIdAfter: e.target.value
											});
										}}
									/>
									
									<div className='deleteColumn'>
										{/* 追加分では何も表示しない */}
									</div>
									
									<div className='cancelColumn'>
										<IconButton
											className='trashBtn'
											onClick={() => handleRemoveUser(user.id)}
											disabled={!userCancelBtn}
										>
											<Trash />
										</IconButton>
									</div>
								</Box>
							))}

							<Button
								className="userSearchBtn"
								variant='secondary'
								onClick={onAddUser}
							>
								{t('adduser')}
							</Button>


						</Grid>

						<Grid container justifyContent="center" className='button'>
							<Button
								className="permissionSettingBtn"
								variant="primary"
								onClick={handleSaveClick}
								disabled={hasEmptyFields}
							>
								{t('save')}
							</Button>
							<Button
								className="cancelBtn"
								variant="secondary"
								onClick={handleCancelClick}
							>
								{t('cancel')}
							</Button>
						</Grid>
					</CardContent>
				</Card>

				{(dialogStatus === DIALOG_STATUS.OK_DIALOG || dialogStatus === DIALOG_STATUS.OK_DIALOG_BACK) && (
					<Dialog
						isOpen={dialogOpen}
						content={dialogContent}
						onOk={onOk}
					/>
				)}
				{(dialogStatus === DIALOG_STATUS.DETAIL_DIALOG || dialogStatus === DIALOG_STATUS.DETAIL_DIALOG_BACK) && (
					<DetailDialog
						isOpen={dialogOpen}
						content={dialogContent}
						detailContent={detailContent}
						onOk={onOk}
					/>
				)}
				{(dialogStatus === DIALOG_STATUS.SAVE_CONFIRM_DIALOG) && (
					<ConfirmDialog
						isOpen={confirmDialogOpen}
						content={confirmDialogContent}
						onOk={saveProcess}
						onCancel={onCancel}
					/>
				)}
				{dialogStatus === DIALOG_STATUS.COMPLETE_DIALOG && (
					<Dialog
						isOpen={dialogOpen}
						content={dialogContent}
						onOk={onOk}
					/>
				)}
				{dialogStatus === DIALOG_STATUS.SAVE_SUCCESS_DIALOG && (
					<Dialog
						isOpen={dialogOpen}
						content={dialogContent}
						onOk={onFinished}
					/>
				)}

			</Container>
		);
	}
}
export default SettingSharedFolder;													
